import { Row } from 'react-table';
import memoize from 'lodash/memoize';
import { Filter } from '../DataGridFilterMenu/DataGridFilterTypes';

type FilterFunction = (cellValue: string | number | boolean | unknown, inputValue: string) => boolean;

const equalsFilter: FilterFunction = (cellValue, inputValue) => {
  return (cellValue as number) === +inputValue;
};

const notEqualsFilter: FilterFunction = (cellValue, inputValue) => {
  return !equalsFilter(cellValue, inputValue) && !!cellValue;
};

const greaterThanFilter: FilterFunction = (cellValue, inputValue) => {
  return (cellValue as number) > +inputValue && !!cellValue;
};

const greaterThanOrEqualFilter: FilterFunction = (cellValue, inputValue) => {
  return (cellValue as number) >= +inputValue && !!cellValue;
};

const lessThanFilter: FilterFunction = (cellValue, inputValue) => {
  return (cellValue as number) < +inputValue && !!cellValue;
};

const lessThanOrEqualFilter: FilterFunction = (cellValue, inputValue) => {
  return (cellValue as number) <= +inputValue && !!cellValue;
};

const isEmptyFilter: FilterFunction = (cellValue, _inputValue) => {
  return !cellValue;
};

const isNotEmptyFilter: FilterFunction = (cellValue, _inputValue) => {
  return !!cellValue;
};

const memoizeResolver = (...args: any[]) => args.join('_'); // eslint-disable-line
const filtersByOperator: Record<string, FilterFunction> = {
  equals: memoize(equalsFilter, memoizeResolver),
  notEquals: memoize(notEqualsFilter, memoizeResolver),
  greaterThan: memoize(greaterThanFilter, memoizeResolver),
  greaterThanOrEqual: memoize(greaterThanOrEqualFilter, memoizeResolver),
  lessThan: memoize(lessThanFilter, memoizeResolver),
  lessThanOrEqual: memoize(lessThanOrEqualFilter, memoizeResolver),
  isEmpty: memoize(isEmptyFilter, memoizeResolver),
  isNotEmpty: memoize(isNotEmptyFilter, memoizeResolver),
};

function runAllFiltersOnRow<T extends Record<string, unknown>>(row: Row<T>, filters: Filter[]) {
  // Find a filter that causes the row to be removed
  return !filters.find((filter) => {
    const filterFunc = filtersByOperator[filter.operator as string];
    return !filterFunc(row.values[filter.columnId as string], filter.value as string);
  });
}

function dataGridNumberFilterFunc<T extends Record<string, unknown> = Record<string, unknown>>(
  rows: Row<T>[],
  columnIds: string[],
  filterValue: Filter[],
): Row<T>[] {
  // Get valid filters
  // NOTE: this means any poorly formed filters will not work.
  const filters = filterValue.filter((filter: Filter) => {
    return !!filter?.operator && !!filter?.columnId && !!filter.value && !!filtersByOperator[filter.operator];
  });

  return rows.filter((row) => {
    return runAllFiltersOnRow(row, filters);
  });
}

export default dataGridNumberFilterFunc;
